import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { NewsItem } from '../lib/types';
import { timeAgo } from '../lib/format';

/**
 * The world ticker.
 *
 * One headline at a time, cross-fading every few seconds, rather than a
 * marquee. A scrolling marquee is the obvious shape for this and the wrong one:
 * you cannot click a moving target, it restarts your reading position on every
 * loop, and screen readers get a stream of half-sentences. A single line that
 * changes on a timer is readable, clickable, and trivially pausable.
 *
 * It pauses on hover and on focus, so a headline never slides away from under
 * the pointer of someone about to click it.
 */

const ROTATE_MS = 6000;

const KIND_ART: Record<NewsItem['kind'], string> = {
  SHINY: '✦',
  LEGENDARY: '★',
  ACHIEVEMENT: '◈',
};

export function NewsTicker() {
  const state = useAsync<{ news: NewsItem[] }>(() => api.get('/community/news'));
  const news = state.data?.news ?? [];

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  /** Drives the fade: flips off then on as the headline swaps. */
  const [shown, setShown] = useState(true);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Keep the index in range when the feed refreshes and shrinks.
  useEffect(() => {
    if (index >= news.length) setIndex(0);
  }, [news.length, index]);

  useEffect(() => {
    // Auto-advance is a convenience, not the only way through: the arrows work
    // regardless, which is what someone who has turned motion down relies on.
    if (paused || reducedMotion || news.length < 2) return;

    timer.current = window.setTimeout(() => {
      setShown(false);
      window.setTimeout(() => {
        setIndex((current) => (current + 1) % news.length);
        setShown(true);
      }, 180);
    }, ROTATE_MS);

    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
    };
  }, [index, paused, reducedMotion, news.length]);

  function step(direction: 1 | -1) {
    setShown(false);
    window.setTimeout(() => {
      setIndex((current) => (current + direction + news.length) % news.length);
      setShown(true);
    }, 120);
  }

  // Nothing has happened lately. Rendering a placeholder bar rather than
  // nothing at all would just be a permanently empty shelf on a quiet server.
  if (news.length === 0) return null;

  const item = news[index]!;

  return (
    <div
      className="pxframe ticker"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="ticker__label" aria-hidden>
        NEWS
      </span>

      {/* polite, not assertive: this is ambient colour and must never interrupt
          what a screen-reader user is doing. */}
      <div className="ticker__viewport" aria-live="polite" aria-atomic="true">
        <Link
          to={item.href}
          className={`ticker__item ${shown ? '' : 'ticker__item--out'} ticker__item--${item.kind.toLowerCase()}`}
        >
          <span className="ticker__icon" aria-hidden>
            {KIND_ART[item.kind]}
          </span>
          {item.sprite && <img className="pixel ticker__sprite" src={item.sprite} alt="" />}
          <span className="ticker__text">{item.text}</span>
          <span className="ticker__time tiny muted">{timeAgo(item.at)}</span>
        </Link>
      </div>

      {news.length > 1 && (
        <div className="ticker__nav">
          <button type="button" onClick={() => step(-1)} aria-label="Previous news item">
            ◀
          </button>
          <span className="ticker__count tiny mono" aria-hidden>
            {index + 1}/{news.length}
          </span>
          <button type="button" onClick={() => step(1)} aria-label="Next news item">
            ▶
          </button>
        </div>
      )}
    </div>
  );
}
